import { fetchAuthSession } from 'aws-amplify/auth'; // Import fetchAuthSession
import { Search } from 'lucide-react'; // Import XCircle for the card close button
import React, { useEffect, useRef, useState } from 'react';
import { useScreenLogging } from '../../hooks/useScreenLogging';

const Assigned = () => {
  // Log screen view
  useScreenLogging('AssignedOfficers', {
    component: 'AssignedOfficers',
    screenType: 'assigned',
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [officers, setOfficers] = useState([]); // This will hold the currently displayed officers (filtered by search)
  const [allFetchedOfficers, setAllFetchedOfficers] = useState([]); // This will hold the complete list of fetched officers
  const [loadingOfficers, setLoadingOfficers] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [error, setError] = useState(null); // State for API errors

  const searchInputRef = useRef(null);
  const suggestionsBoxRef = useRef(null);

  // Base URL for all API calls
  const BASE_URL = 'https://api.innoworx.ai/Stage';

  // Formats ISO timestamp to 'dd/MM - h:mma' in local time. Returns 'N/A' if invalid.
  const formatTimestamp = timestamp => {
    if (!timestamp || timestamp === 'N/A') return 'N/A';
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return 'N/A';
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      let hours = date.getHours();
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12;
      if (hours === 0) hours = 12; // 12-hour clock
      return `${day}/${month} - ${hours}:${minutes}${ampm}`;
    } catch (e) {
      return 'N/A';
    }
  };

  // Function to get the authentication token
  const getAuthToken = async () => {
    try {
      const { tokens } = await fetchAuthSession();
      if (tokens && tokens.idToken) {
        return tokens.idToken.toString();
      } else {
        console.error('No ID Token found in the current session.');
        return null;
      }
    } catch (error) {
      console.error('Error fetching authentication session:', error);
      return null;
    }
  };

  // Main useEffect for fetching and merging officer data
  useEffect(() => {
    const fetchAssignedOfficers = async () => {
      setLoadingOfficers(true);
      setError(null);

      if (BASE_URL === 'YOUR_API_BASE_URL') {
        setError(
          'API Base URL is not configured. Please update BASE_URL in Assigned.js.'
        );
        setOfficers([]);
        setAllFetchedOfficers([]);
        setFilteredSuggestions([]);
        setLoadingOfficers(false);
        return;
      }

      try {
        const token = await getAuthToken();
        if (!token) {
          setError(
            'Authentication token is missing. Please ensure a user is logged in.'
          );
          setOfficers([]);
          setAllFetchedOfficers([]);
          setFilteredSuggestions([]);
          setLoadingOfficers(false);
          return;
        }

        const headers = {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        };

        // --- API Call 1: Get officer status list (for basic info and primary list of officers) ---
        const statusListUrl = `${BASE_URL}/user/officer/status-list`;
        console.log('Fetching officer status list from:', statusListUrl);
        const statusListResponse = await fetch(statusListUrl, {
          method: 'GET',
          headers: headers,
        });

        if (!statusListResponse.ok) {
          const errorText = await statusListResponse.text();
          throw new Error(
            `HTTP error! status: ${statusListResponse.status}, message: ${errorText} while fetching officer status list`
          );
        }
        const statusListData = await statusListResponse.json();
        let allOfficersFromStatusList = statusListData.users;

        if (!Array.isArray(allOfficersFromStatusList)) {
          console.warn(
            "API response for /user/officer/status-list is not an array (expected 'users' array):",
            statusListData
          );
          allOfficersFromStatusList = [];
        }

         // --- API Call 2: Get all users with duty data (to determine 'on_duty' status) ---
        const dutyDataUrl = `${BASE_URL}/dashboard/user/get-all-with-duty-data`;
        console.log('Fetching duty data from:', dutyDataUrl);
        const dutyDataResponse = await fetch(dutyDataUrl, {
          method: 'GET',
          headers: headers,
        });

        if (!dutyDataResponse.ok) {
          const errorText = await dutyDataResponse.text();
          throw new Error(
            `HTTP error! status: ${dutyDataResponse.status}, message: ${errorText} while fetching duty data`
          );
        }
        const dutyData = await dutyDataResponse.json();
        let allUsersWithDutyData = dutyData.users;

        if (!Array.isArray(allUsersWithDutyData)) {
          console.warn(
            "API response for /dashboard/user/get-all-with-duty-data is not an array (expected 'users' array):",
            dutyData
          );
          allUsersWithDutyData = [];
        }

        // Create maps for quick lookup
        const statusListMap = new Map();
        allOfficersFromStatusList.forEach(officer => {
          statusListMap.set(officer.user_id, officer);
        });

        const dutyDataMap = new Map();
        allUsersWithDutyData.forEach(user => {
          dutyDataMap.set(user.user_id, user);
        });

        // --- Merge data and conditionally fetch detailed status ---
        const finalOfficersDataPromises = Array.from(statusListMap.keys()).map(
          async userId => {
            const officerBasicInfo = statusListMap.get(userId);
            const officerDutyInfo = dutyDataMap.get(userId);

            let siteStatus = 'out-of-site'; // Default
            let siteName = 'N/A';
            let address = 'N/A';
            let lastLocationUpdate = 'N/A';
            let onDuty = officerDutyInfo ? officerDutyInfo.on_duty : false;
            let offDuty = officerDutyInfo ? officerDutyInfo.off_duty : true; // Default to offDuty if no info
            let scheduledNow = officerDutyInfo ? officerDutyInfo.scheduled_now : false; // Check if currently scheduled

            // If the officer is marked as 'on_duty' from the duty data API,
            // then fetch their individual real-time status for site details.
            if (onDuty) {
              try {
                // API Call 3: Get individual officer status for detailed site info
                const individualStatusUrl = `${BASE_URL}/user/officer/status?user_id=${userId}`;
                console.log(
                  'Fetching individual officer status from:',
                  individualStatusUrl
                );
                const individualStatusResponse = await fetch(
                  individualStatusUrl,
                  {
                    method: 'GET',
                    headers: headers,
                  }
                );

                if (!individualStatusResponse.ok) {
                  const errorText = await individualStatusResponse.text();
                  console.error(
                    `HTTP error! status: ${individualStatusResponse.status}, message: ${errorText} for user ${userId} when fetching individual status`
                  );
                  // If individual status fetch fails, or not in-site, keep N/A
                } else {
                  const individualStatusData =
                    await individualStatusResponse.json();
                  siteStatus = individualStatusData.status || 'out-of-site';

                  if (siteStatus === 'in-site' && individualStatusData.site) {
                    siteName = individualStatusData.site.site_name || 'N/A';
                    address = individualStatusData.site.address || 'N/A';
                  }
                  if (individualStatusData.location_timestamp) {
                    lastLocationUpdate = formatTimestamp(
                      individualStatusData.location_timestamp
                    );
                  }
                }
              } catch (individualError) {
                console.error(
                  `Error fetching individual status for ${officerBasicInfo.user_name} (${userId}):`,
                  individualError
                );
              }
            }

            // If we still don't have a timestamp (e.g., not on duty or individual fetch failed),
            // fall back to duty data timestamp if available
            if (
              (!lastLocationUpdate || lastLocationUpdate === 'N/A') &&
              officerDutyInfo &&
              officerDutyInfo.location_timestamp
            ) {
              lastLocationUpdate = formatTimestamp(
                officerDutyInfo.location_timestamp
              );
            }

            return {
              id: userId,
              name: officerBasicInfo.user_name,
              employee_id: officerBasicInfo.employee_id,
              spec_role: officerBasicInfo.spec_role,
              on_duty: onDuty,
              off_duty: offDuty,
              scheduled_now: scheduledNow, // Add scheduled_now status
              status: siteStatus, // This is the 'in-site'/'out-of-site' status from the third API call
              siteName: siteName,
              address: address,
              lastLocationUpdate: lastLocationUpdate,
            };
          }
        );

        const finalOfficersData = await Promise.all(finalOfficersDataPromises);

        // Filter officers to display only those who are currently scheduled (scheduled_now = true)
        const assignedOfficersToDisplay = finalOfficersData.filter(officer => officer.scheduled_now);


        setAllFetchedOfficers(finalOfficersData); // Store the full list
        setOfficers(assignedOfficersToDisplay); // Display only scheduled officers
        setFilteredSuggestions(
          assignedOfficersToDisplay.map(officer => officer.name)
        );
      } catch (err) {
        console.error('Error in fetchAssignedOfficers:', err);
        setError(
          `Failed to load assigned officers. Please try again. Error: ${err.message}`
        );
        setOfficers([]);
        setAllFetchedOfficers([]);
        setFilteredSuggestions([]);
      } finally {
        setLoadingOfficers(false);
      }
    };

    const timer = setTimeout(() => {
      fetchAssignedOfficers();
    }, 300);

    return () => clearTimeout(timer);
  }, [BASE_URL]); // Dependencies for initial fetch

  // useEffect for filtering based on searchTerm, using `allFetchedOfficers`
  useEffect(() => {
    const lowerCaseSearchTerm = searchTerm.toLowerCase();
    // Filter from the `allFetchedOfficers` first, then apply the on_duty filter
    const filteredFromAll = allFetchedOfficers.filter(
      officer =>
        (officer.name &&
          officer.name.toLowerCase().includes(lowerCaseSearchTerm)) ||
        (officer.employee_id &&
          officer.employee_id.toLowerCase().includes(lowerCaseSearchTerm)) ||
        (officer.spec_role &&
          officer.spec_role.toLowerCase().includes(lowerCaseSearchTerm))
    );

    // Then, apply the scheduled_now filter to the search results
    const finalFilteredOfficers = filteredFromAll.filter(
      officer => officer.scheduled_now
    );

    if (lowerCaseSearchTerm === '') {
      setOfficers(allFetchedOfficers.filter(officer => officer.scheduled_now)); // If search is empty, show all scheduled
      setFilteredSuggestions(
        allFetchedOfficers.filter(officer => officer.scheduled_now).map(o => o.name)
      );
    } else {
      setOfficers(finalFilteredOfficers); // Update displayed officers
      setFilteredSuggestions(finalFilteredOfficers.map(o => o.name)); // Update suggestions
    }
  }, [searchTerm, allFetchedOfficers]);

  const handleSearchInputChange = e => {
    setSearchTerm(e.target.value);
    setShowSuggestions(e.target.value.length > 0);
  };

  const handleSuggestionClick = suggestionName => {
    setSearchTerm(suggestionName);
    setShowSuggestions(false);
    // The useEffect for `searchTerm` will now handle filtering `officers`
    // based on `allFetchedOfficers` and the selected `suggestionName`.
  };

  return (
    <div className="flex flex-col flex-1 rounded-xl shadow-[0px_15px_30px_0px_rgba(0,0,0,0.10)] shadow-[2px_2px_6px_0px_rgba(211,211,211,0.10)] overflow-hidden">

      {/* Header Title */}
      <div className="mb-6">
        <h1 className="text-black text-3xl font-bold font-['Figtree'] leading-10">
          Assigned Officers
        </h1>
      </div>

      {/* Search Bar */}
      <div
        data-property-1="Normal"
        className="w-96 h-14 pl-4 pr-3.5 py-2.5 mb-6 bg-neutral-50/80 rounded-[10px] shadow-[0px_15px_30px_0px_rgba(0,0,0,0.10)] shadow-[0px_5px_20.100000381469727px_0px_rgba(255,255,255,0.35)] backdrop-blur-[30px] flex flex-col justify-center items-start gap-2.5 relative"
      >
        <div className="inline-flex justify-start items-center gap-4 w-full">
          <Search className="w-6 h-6 text-neutral-400" />
          <input
            type="text"
            placeholder="Search by Employee name or id"
            className="flex-grow bg-transparent outline-none text-neutral-900 text-lg font-bold font-['Figtree'] leading-snug placeholder-neutral-400"
            value={searchTerm}
            onChange={handleSearchInputChange}
            onFocus={() =>
              setShowSuggestions(
                searchTerm.length > 0 && filteredSuggestions.length > 0
              )
            }
            ref={searchInputRef}
          />
        </div>
        {/* Search Suggestions Dropdown */}
        {showSuggestions &&
          searchTerm.length > 0 &&
          filteredSuggestions.length > 0 && (
            <div
              ref={suggestionsBoxRef}
              className="absolute z-10 w-full bg-neutral-50/90 rounded-[10px] top-full mt-2 left-0 shadow-lg max-h-60 overflow-y-auto"
            >
              <div className="inline-flex flex-col justify-start items-center gap-3 w-full p-3">
                {filteredSuggestions.map((suggestion, index) => (
                  <React.Fragment key={index}>
                    <div
                      className="self-stretch inline-flex justify-center items-center gap-2.5 cursor-pointer hover:bg-gray-100 p-2 rounded-md"
                      onClick={() => handleSuggestionClick(suggestion)}
                    >
                      <div className="w-full justify-start text-black text-lg font-normal font-['Open_Sans'] leading-relaxed">
                        {suggestion}
                      </div>
                    </div>
                    {index < filteredSuggestions.length - 1 && (
                      <div className="self-stretch h-0 outline outline-1 outline-offset-[-0.50px] outline-neutral-400"></div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}
      </div>

      {/* Officer Cards Container */}
      <div className="flex flex-wrap justify-start items-start gap-4 w-full overflow-y-auto flex-1">
        {loadingOfficers ? (
          <div className="flex items-center justify-center w-full h-full text-gray-500">
            Loading Scheduled Officers...
          </div>
        ) : error ? (
          <div className="flex items-center justify-center w-full h-full text-red-500">
            {error}
          </div>
        ) : officers.length > 0 ? (
          officers.map(officer => (
            <div
              key={officer.id}
              className="p-4 bg-neutral-50/50 rounded-xl shadow-[-2px_-2px_6px_0px_rgba(211,211,211,0.10)] shadow-[2px_2px_6px_0px_rgba(211,211,211,0.10)] backdrop-blur-[20px] inline-flex flex-col justify-start items-start gap-4 flex-grow-0 flex-shrink-0 basis-[300px]"
            >
              {/* Officer Basic Info */}
              <div className="w-full flex flex-col justify-start items-start gap-3">
                <div className="self-stretch inline-flex justify-between items-center">
                  <div className="justify-center text-neutral-900 text-lg font-bold font-['Figtree'] leading-snug">
                    {officer.name}
                  </div>
                  {/* Display Shift Status based on on_duty and scheduled_now properties */}
                  <div className="px-3 py-1.5 bg-white rounded-xl flex justify-start items-center gap-2">
                    <div
                      className={`w-3.5 h-3.5 rounded-full ${
                        officer.on_duty 
                          ? 'bg-green-700' 
                          : officer.scheduled_now 
                            ? 'bg-yellow-500' 
                            : 'bg-red-500'
                      }`}
                    />
                    <div
                      className={`justify-center text-base font-normal font-['Open_Sans'] leading-tight ${
                        officer.on_duty 
                          ? 'text-green-700' 
                          : officer.scheduled_now 
                            ? 'text-yellow-600' 
                            : 'text-red-500'
                      }`}
                    >
                      {officer.on_duty 
                        ? 'On Duty' 
                        : officer.scheduled_now 
                          ? 'Scheduled' 
                          : 'Off Duty'
                      }
                    </div>
                  </div>
                </div>
                <div className="self-stretch inline-flex justify-between items-center">
                  <div className="text-center justify-center text-neutral-500 text-base font-semibold font-['Open_Sans'] leading-tight">
                    {officer.spec_role}
                  </div>
                  <div className="text-right justify-center text-neutral-900 text-lg font-normal font-['Open_Sans'] leading-relaxed">
                    ID : {officer.employee_id}
                  </div>
                </div>
              </div>

              {/* Site Information Card (Conditional Rendering based on 'in-site' status) */}
              {officer.status === 'in-site' && (
                <div className="w-full p-3 bg-green-50/50 rounded-xl shadow-[inset_0px_4px_12px_0px_rgba(0,0,0,0.05)] flex flex-col justify-start items-start gap-1.5">
                  <div className="self-stretch text-neutral-900 text-lg font-normal font-['Open_Sans'] leading-relaxed">
                    Site Information
                  </div>
                  <div className="self-stretch justify-between items-center inline-flex w-full">
                    <div className="text-neutral-700 text-base font-normal font-['Open_Sans'] leading-snug">
                      Site Name
                    </div>
                    <div className="text-right justify-center text-neutral-900 text-lg font-normal font-['Open_Sans'] leading-relaxed">
                      {officer.siteName}
                    </div>
                  </div>
                  <div className="self-stretch justify-between items-center inline-flex w-full">
                    <div className="text-neutral-700 text-base font-normal font-['Open_Sans'] leading-snug">
                      Address
                    </div>
                    <div className="text-right justify-center text-neutral-900 text-lg font-normal font-['Open_Sans'] leading-relaxed">
                      {officer.address}
                    </div>
                  </div>
                  <div className="self-stretch justify-between items-center inline-flex w-full">
                    <div className="text-neutral-700 text-base font-normal font-['Open_Sans'] leading-snug">
                      Last Location Updated
                    </div>
                    <div className="text-right justify-center text-neutral-900 text-lg font-normal font-['Open_Sans'] leading-relaxed">
                      {officer.lastLocationUpdate}
                    </div>
                  </div>
                  {/* IN SITE indicator moved inside the card */}
                  <div className="px-3 py-1.5 bg-white rounded-xl inline-flex justify-start items-center gap-2 mt-2">
                    <div className="w-3.5 h-3.5 bg-green-700 rounded-full" />
                    <div className="justify-center text-green-700 text-base font-normal font-['Open_Sans'] leading-tight">
                      In Site
                    </div>
                  </div>
                </div>
              )}
              {/* Display "Out Of Site" N/A card if officer is not 'in-site' */}
              {officer.status !== 'in-site' && (
                <div className="w-full p-3 bg-neutral-50/10 rounded-xl shadow-[inset_0px_4px_12px_0px_rgba(0,0,0,0.05)] flex flex-col justify-start items-start gap-1.5">
                  <div className="self-stretch justify-center text-neutral-900 text-lg font-normal font-['Open_Sans'] leading-relaxed">
                    Site Name : N/A
                  </div>
                  <div className="self-stretch justify-center text-neutral-900 text-lg font-normal font-['Open_Sans'] leading-relaxed">
                    Address : N/A
                  </div>
                  <div className="self-stretch justify-center text-neutral-900 text-lg font-normal font-['Open_Sans'] leading-relaxed">
                    Last Location Updated : {officer.lastLocationUpdate}
                  </div>
                  {/* OUT OF SITE indicator is already here, matching the image */}
                  <div className="px-3 py-1.5 bg-white rounded-xl inline-flex justify-start items-center gap-2">
                    <div className="w-3.5 h-3.5 bg-red-700 rounded-full" />
                    <div className="justify-center text-red-700 text-base font-normal font-['Open_Sans'] leading-tight">
                      Out Of Site
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="flex items-center justify-center w-full h-full text-gray-500">
            No scheduled officers found. Officers will appear here when they have scheduled_now = true.
          </div>
        )}
      </div>
    </div>
  );
};

export default Assigned;
